/**
 * useQuotaGate — catch HTTP 402 `quota_exceeded` responses and drive
 * QuotaExceededModal / QuotaBanner from them.
 *
 * Usage:
 *   const gate = useQuotaGate()
 *   const res = await gate.fetch(url, { method: 'POST', body })
 *   if (!res) return            // blocked by the barrier, modal is open
 *   ...
 *   <QuotaExceededModal open={gate.open} quota={gate.quota} onClose={gate.dismiss} />
 */

import { useCallback, useState } from 'preact/hooks'
import { parseQuotaError, parseQuotaResponse, parseQuotaException } from './parseQuotaError.js'

export function useQuotaGate({ onBlocked } = {}) {
  const [quota, setQuota] = useState(null)

  const dismiss = useCallback(() => setQuota(null), [])

  const capture = useCallback((parsed) => {
    if (!parsed) return null
    setQuota(parsed)
    onBlocked?.(parsed)
    return parsed
  }, [onBlocked])

  /** Capture from an already parsed JSON body (+ optional status). */
  const captureBody = useCallback((body, status) => capture(parseQuotaError(body, status)), [capture])

  /** Capture from a fetch Response without consuming the caller's body. */
  const captureResponse = useCallback(async (res) => capture(await parseQuotaResponse(res)), [capture])

  /** Capture from a thrown fetch-wrapper error (err.status / err.body). */
  const captureError = useCallback((err) => capture(parseQuotaException(err)), [capture])

  /**
   * fetch() wrapper: resolves to null (and opens the gate) on a quota barrier,
   * otherwise returns the untouched Response.
   */
  const guardedFetch = useCallback(async (input, init) => {
    const res = await fetch(input, init)
    const parsed = await parseQuotaResponse(res)
    if (parsed) {
      capture(parsed)
      return null
    }
    return res
  }, [capture])

  return {
    quota,
    open: !!quota,
    blocking: !!quota?.blocking,
    dismiss,
    captureBody,
    captureResponse,
    captureError,
    fetch: guardedFetch
  }
}

export default useQuotaGate
